import type { CSSProperties, ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import type { GlobalToken } from 'antd/es/theme/interface';
import { CardContent, MetricCardEcho } from '@/components/cards/common';
import type { ContentOverviewResponse } from '@/types/content';
import { InfoCircleOutlined } from '@ant-design/icons';
import { PlatformIconEcho } from '@/components/PlatformIconEcho';
import { Flex, theme, Tooltip, Typography } from 'antd';

const DEFAULT_SECTION_LABEL = 'Podcast Players';

type DataType = ContentOverviewResponse;

export interface PlayerSection {
  label?: string;
  players: string[];
}

export interface ContentOverviewMetricData {
  value: number;
  valueDiff?: number;
}

interface ContentPerformanceOverviewCardProps {
  style?: CSSProperties;
  label: string;
  tooltipTitle: ReactNode;
  queryResult: UseQueryResult<ContentOverviewResponse, Error>;
  selectMetric: (data: ContentOverviewResponse) => ContentOverviewMetricData;
  playerSections?: PlayerSection[];
  podcastPlayersTooltipContent?: ReactNode;
}

const ContentOverviewMetricLabelRow = ({
  label,
  tooltipTitle,
  playerSections,
  podcastPlayersTooltipContent,
}: {
  label: string;
  tooltipTitle: ReactNode;
  playerSections: PlayerSection[];
  podcastPlayersTooltipContent?: ReactNode;
}) => {
  const { token } = theme.useToken();

  const labelRowStyle: CSSProperties = {
    minHeight: token.controlHeightSM,
  };

  const secondaryNoWrapTextStyle: CSSProperties = {
    color: token.colorTextSecondary,
    whiteSpace: 'nowrap',
  };

  const secondaryTextStyle: CSSProperties = {
    ...secondaryNoWrapTextStyle,
    fontSize: token.fontSizeSM,
  };

  const podcastPlayersStyle: CSSProperties = {
    height: token.size,
  };

  const sectionLabelStyle: CSSProperties = {
    ...secondaryTextStyle,
    fontWeight: 300,
  };

  const renderTooltip = (title: ReactNode, content: ReactNode, key?: string): ReactNode => (
    <Tooltip key={key} title={title} placement="bottom">
      <Flex align="center" style={podcastPlayersStyle}>
        {content}
      </Flex>
    </Tooltip>
  );

  return (
    <Flex style={labelRowStyle} justify="space-between" wrap="wrap" gap={token.marginXXS}>
      <Flex align="center" gap={token.marginXXS}>
        <Typography.Text style={secondaryNoWrapTextStyle}>{label}</Typography.Text>
        <Tooltip title={tooltipTitle} placement="bottomRight">
          <span data-testid="overview-metric-info">
            <InfoCircleOutlined
              style={{
                fontSize: token.fontSize,
                color: token.colorTextDescription,
                marginInlineStart: token.marginXXS,
              }}
            />
          </span>
        </Tooltip>
      </Flex>
      {playerSections.length > 0 && (
        <Flex>
          {playerSections.map((section, index) => {
            const sectionLabel = section.label ?? DEFAULT_SECTION_LABEL;
            const isSinglePlayer = section.players.length === 1;
            const noPlayers = section.players.length === 0;
            const showTooltipOnIcon = isSinglePlayer && !!podcastPlayersTooltipContent;
            const showMoreTrigger = !isSinglePlayer && !!podcastPlayersTooltipContent;

            return noPlayers ? null : (
              <Flex key={sectionLabel ?? index} align="center" gap={token.marginXS}>
                <Flex align="center" style={podcastPlayersStyle}>
                  <Typography.Text style={sectionLabelStyle}>{sectionLabel}</Typography.Text>
                </Flex>
                <Flex align="center" gap={token.marginXXS}>
                  {section.players.map((appName) =>
                    renderTooltip(
                      showTooltipOnIcon ? podcastPlayersTooltipContent! : appName,
                      <PlatformIconEcho name={appName} />,
                      appName
                    )
                  )}
                  {showMoreTrigger &&
                    renderTooltip(
                      podcastPlayersTooltipContent!,
                      <Typography.Text style={secondaryTextStyle}>....</Typography.Text>
                    )}
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      )}
    </Flex>
  );
};

const getPercentageDiffFormatting = (
  valueDiff: number | undefined,
  token: GlobalToken
): ReactNode => {
  if (valueDiff == null) return null;

  const formatted =
    valueDiff === 0 ? '0%' : (valueDiff > 0 ? '+' : '') + valueDiff.toFixed(1) + '%';
  let textColor: string;
  switch (Math.sign(valueDiff)) {
    case 1:
      textColor = token.colorSuccess;
      break;
    case -1:
      textColor = token.colorError;
      break;
    default:
      textColor = token.colorTextSecondary;
      break;
  }

  const badgeStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: token.borderRadiusSM,
    paddingLeft: token.paddingXS,
    paddingRight: token.paddingXS,
    paddingTop: 2,
    paddingBottom: 2,
    fontSize: token.fontSizeSM,
    color: textColor,
    whiteSpace: 'nowrap',
  };

  return <Typography.Text style={badgeStyle}>{formatted}</Typography.Text>;
};

const ContentPerformanceOverviewCardEcho = ({
  style,
  label,
  tooltipTitle,
  queryResult,
  selectMetric,
  playerSections = [],
  podcastPlayersTooltipContent,
}: ContentPerformanceOverviewCardProps) => {
  const { token } = theme.useToken();

  return (
    <MetricCardEcho
      style={style}
      dataTestId="content-performance-metric-card-data"
      title={
        <ContentOverviewMetricLabelRow
          label={label}
          tooltipTitle={tooltipTitle}
          playerSections={playerSections}
          podcastPlayersTooltipContent={podcastPlayersTooltipContent}
        />
      }
      body={
        <CardContent<DataType>
          {...queryResult}
          renderData={(overviewData: DataType) => {
            const metric = selectMetric(overviewData);
            return (
              <Flex align="flex-start" gap={token.marginXS}>
                <Typography.Title
                  data-testid="overview-metric-value"
                  level={3}
                  ellipsis={{ rows: 1 }}
                  style={{ margin: 0 }}
                >
                  {(metric.value ?? 0).toLocaleString()}
                </Typography.Title>
                {getPercentageDiffFormatting(metric.valueDiff, token)}
              </Flex>
            );
          }}
        />
      }
    />
  );
};

export default ContentPerformanceOverviewCardEcho;
